import { useState, useEffect, useRef } from 'react';
import { Send, Loader2, MessageSquare, Link, History, Plus, Trash2, Menu, X } from 'lucide-react';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-sql';
import 'prismjs/themes/prism-tomorrow.css';
import { useAuth } from '../context/AuthContext';

// Fun rotating thinking messages (generic, no implied sequence)
const THINKING_MESSAGES = [
  'Thinking...',
  'Crunching numbers...',
  'Consulting the data gods...',
  'Working on it...',
  'Brewing some SQL magic...',
  'Deep in thought...',
  'Processing...',
  'Asking BigQuery nicely...',
  'Joining tables in holy matrimony...',
  'SELECT-ing my words carefully...',
  'WHERE did I put that data...',
  'GROUP BY-ing my thoughts...',
  'ORDER BY importance...',
  'Running faster than a full table scan...',
  'Optimizing query plan...',
  'Warming up the warehouse...',
  'Summoning the aggregation spirits...',
  'Parsing your intent...',
  'Cross-referencing dimensions...',
  'Negotiating with NULL values...',
  'Teaching facts to dimensions...',
  'Untangling the star schema...',
  'Counting rows, not sheep...',
  'Applying some window functions...',
  'CTEs assemble!...',
  'Recursive thinking in progress...',
  'Partitioning the problem...',
  'Materializing views...',
  'Indexing my thoughts...',
  'Running EXPLAIN ANALYZE on life...',
  'Fetching from the cloud...',
  'Avoiding Cartesian products...',
  'Respecting data integrity...',
  'Checking foreign key relationships...',
  'Normalizing expectations...',
  'Denormalizing for speed...',
  'Pivoting perspectives...',
  'Unpivoting assumptions...',
  'Distinct-ly working on it...',
  'Coalescing ideas...',
  'CASE WHEN genius THEN answer...',
  'Casting data types responsibly...',
  'Truncating unnecessary complexity...',
  'Rounding to a solution...',
  'Subquerying the subconscious...',
  'Correlating data points...',
  'Anti-joining bad ideas...',
  'Semi-joining good ones...',
  'Executing with precision...',
  'Committing to an answer...',
  'Rolling back doubts...',
  'Vacuuming stale thoughts...',
  'Analyzing statistics...',
  'Building the perfect query...',
  'Almost done, pinky promise...',
];

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sql?: string;
  execution_time_ms?: number;
  error?: string;
  pending?: boolean;  // True if this is a pending assistant response
  started_at?: number;  // Timestamp when query started (for elapsed time)
}

interface Agent {
  name: string;
  path: string;
  display_name: string;
}

interface Model {
  name: string;
  display_name: string;
  description: string;
}

interface ChatSession {
  id: string;
  title: string;
  agent_path: string;
  model: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export default function AgentChat() {
  const { user, getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingStage, setThinkingStage] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(true);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingModels, setLoadingModels] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);  // Mobile sidebar state
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Cycle through thinking messages while loading
  useEffect(() => {
    if (!isLoading) {
      setThinkingStage(0);
      setElapsedTime(0);
      return;
    }

    // Store start time when loading begins
    const loadingStartTime = Date.now();

    // Update elapsed time every second
    const timeInterval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - loadingStartTime) / 1000));
    }, 1000);

    // Pick random message every 15 seconds
    const stageInterval = setInterval(() => {
      setThinkingStage(prev => {
        let next;
        do {
          next = Math.floor(Math.random() * THINKING_MESSAGES.length);
        } while (next === prev && THINKING_MESSAGES.length > 1);
        return next;
      });
    }, 15000);

    return () => {
      clearInterval(timeInterval);
      clearInterval(stageInterval);
    };
  }, [isLoading]);

  useEffect(() => {
    loadAgents();
    loadModels();
    if (user) {
      loadChatSessions();
    }
  }, [user]);

  const loadChatSessions = async () => {
    if (!user) return;
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/chat/sessions', { headers });
      const data = await response.json();
      setChatSessions(data.sessions || []);
    } catch (err) {
      console.error('Failed to load chat sessions:', err);
    }
  };

  const loadSession = async (sessionIdToLoad: string) => {
    if (!user) return;

    // Stop any existing polling for a different session
    pollingSessionRef.current = null;

    // Close sidebar on mobile when loading a session
    setSidebarOpen(false);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/chat/sessions/${sessionIdToLoad}`, { headers });
      const data = await response.json();
      if (data.session) {
        setCurrentSessionId(sessionIdToLoad);
        setSessionId(data.session.adk_session_id || null);
        const loadedMessages = data.session.messages || [];
        setMessages(loadedMessages);
        setSelectedAgent(data.session.agent_path);
        setSelectedModel(data.session.model);

        // Check if there's a pending message - if so, set loading state
        const hasPendingMessage = loadedMessages.some((m: Message) => m.pending);
        if (hasPendingMessage || data.session.has_pending) {
          setIsLoading(true);
          // Start polling for completion
          pollForCompletion(sessionIdToLoad);
        } else {
          // Make sure we're not showing loading for a completed session
          setIsLoading(false);
        }
      }
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  };

  // Poll for pending message completion
  // Uses a ref to track the session being polled so we don't overwrite other sessions
  const pollingSessionRef = useRef<string | null>(null);

  const pollForCompletion = async (sessionIdToPoll: string) => {
    const maxAttempts = 3600; // 60 minutes max (every 1 second)
    let attempts = 0;
    pollingSessionRef.current = sessionIdToPoll;

    const poll = async () => {
      // Stop polling if we switched to a different session
      if (pollingSessionRef.current !== sessionIdToPoll) {
        return;
      }

      if (attempts >= maxAttempts) {
        setIsLoading(false);
        return;
      }
      attempts++;

      try {
        const headers = await getAuthHeaders();
        const response = await fetch(`/api/chat/sessions/${sessionIdToPoll}`, { headers });
        const data = await response.json();

        if (data.session) {
          const loadedMessages = data.session.messages || [];
          const stillPending = loadedMessages.some((m: Message) => m.pending) || data.session.has_pending;

          if (!stillPending) {
            // Done - response received, now update messages
            if (pollingSessionRef.current === sessionIdToPoll) {
              setMessages(loadedMessages);
              setIsLoading(false);
            }
            loadChatSessions(); // Refresh history
            return;
          }
          // Still pending - update messages to show progress updates
          // The pending message may have content (progress text) that we want to show
          if (pollingSessionRef.current === sessionIdToPoll) {
            setMessages(loadedMessages);
          }
        }

        // Still pending, poll again
        setTimeout(poll, 1000);
      } catch (err) {
        console.error('Poll error:', err);
        if (pollingSessionRef.current === sessionIdToPoll) {
          setIsLoading(false);
        }
      }
    };

    poll();
  };

  // Note: saveSession is no longer called from sendMessage - the backend handles it
  // This is only used for manual session updates if needed in the future

  const deleteSession = async (sessionId: string) => {
    if (!user) return;
    try {
      const headers = await getAuthHeaders();
      await fetch(`/api/chat/sessions/${sessionId}`, {
        method: 'DELETE',
        headers
      });
      if (currentSessionId === sessionId) {
        startNewChat();
      }
      loadChatSessions();
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  };

  const startNewChat = () => {
    pollingSessionRef.current = null;  // Stop any existing polling
    setMessages([]);
    setSessionId(null);
    setCurrentSessionId(null);
    setIsLoading(false);  // Stop any loading state from previous session
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadAgents = async () => {
    setLoadingAgents(true);
    try {
      const response = await fetch('/api/chat/agents');
      const data = await response.json();
      // Filter out agents with "eval" in their name or path (evaluation-only agents)
      const chatAgents = (data.agents || []).filter((agent: Agent) => {
        const nameLower = agent.name.toLowerCase();
        const pathLower = agent.path.toLowerCase();
        const displayLower = agent.display_name.toLowerCase();
        return !nameLower.includes('eval') && !pathLower.includes('eval') && !displayLower.includes('eval');
      });
      setAgents(chatAgents);
      // Select the latest added agent (last in the list) as default
      if (chatAgents.length > 0 && !selectedAgent) {
        setSelectedAgent(chatAgents[chatAgents.length - 1].path);
      }
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadModels = async () => {
    setLoadingModels(true);
    try {
      const response = await fetch('/api/chat/models');
      const data = await response.json();
      setModels(data.models || []);
      // Auto-select latest model: prefer gemini-3, then gemini-2.5, prefer pro over flash
      if (data.models && data.models.length > 0 && !selectedModel) {
        const modelList = data.models as Model[];
        // Priority: gemini-3-pro > gemini-3-flash > gemini-2.5-pro > gemini-2.5-flash > first
        const gemini3Pro = modelList.find((m) => m.name.includes('gemini-3') && m.name.includes('pro'));
        const gemini3Flash = modelList.find((m) => m.name.includes('gemini-3') && m.name.includes('flash'));
        const gemini25Pro = modelList.find((m) => m.name.includes('gemini-2.5') && m.name.includes('pro'));
        const gemini25Flash = modelList.find((m) => m.name.includes('gemini-2.5') && m.name.includes('flash'));
        const bestModel = gemini3Pro || gemini3Flash || gemini25Pro || gemini25Flash || modelList[0];
        setSelectedModel(bestModel.name);
      }
    } catch (err) {
      console.error('Failed to load models:', err);
    } finally {
      setLoadingModels(false);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || !selectedAgent) return;

    const question = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    // Optimistically show user message with pending response
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: question,
    };
    const pendingMessage: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: '',
      pending: true,
    };
    setMessages([...messages, userMessage, pendingMessage]);

    try {
      const headers = await getAuthHeaders();

      // Send to backend - it processes in background and returns immediately
      const response = await fetch('/api/chat/send', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: currentSessionId,
          question: question,
          agent_path: selectedAgent,
          model: selectedModel,
          adk_session_id: sessionId,
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to send message');
      }

      // Update session ID if new
      if (result.session_id && !currentSessionId) {
        setCurrentSessionId(result.session_id);
      }

      // Start polling for completion
      pollForCompletion(result.session_id);

    } catch (err: any) {
      // Show error immediately
      setMessages([...messages, userMessage, {
        id: pendingMessage.id,
        role: 'assistant',
        content: `Error: ${err.message}`,
        error: err.message,
        pending: false,
      }]);
      setIsLoading(false);
    }
  };


  // Reset session when agent or model changes (session is tied to agent+model)
  useEffect(() => {
    if (sessionId) {
      setSessionId(null);
    }
  }, [selectedAgent, selectedModel]);

  const selectedModelData = models.find(m => m.name === selectedModel);
  const selectedAgentData = agents.find(a => a.path === selectedAgent);

  return (
    <div className="flex flex-col lg:flex-row h-full bg-gradient-to-br from-background via-background to-primary/5">
      {/* Mobile Header with hamburger menu */}
      <div className="lg:hidden flex-shrink-0 bg-background border-b border-primary/30 px-4 py-2 flex items-center gap-3">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <span className="font-semibold text-foreground">Chat</span>
        </div>
        <button
          onClick={startNewChat}
          className="ml-auto p-2 bg-primary/20 text-primary border border-primary/30 rounded-lg hover:bg-primary/30 transition-colors"
          title="New Chat"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Configuration */}
      <aside className={`
        w-80 border-r border-primary/30 glass-header flex flex-col flex-shrink-0
        fixed lg:relative inset-y-0 left-0 z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-bold bg-gradient-to-r from-primary to-orange-400 bg-clip-text text-transparent">
                Chat
              </h2>
            </div>
            {/* Close button - only visible on mobile */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-primary/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Agent Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2 text-foreground">
              Select Agent
            </label>
            {loadingAgents ? (
              <div className="w-full p-3 border rounded-lg bg-background border-primary/30 flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading agents...
              </div>
            ) : (
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                disabled={isLoading}
                className="w-full p-3 border rounded-lg bg-background text-foreground border-primary/30 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {agents.map((agent) => (
                  <option key={agent.path} value={agent.path}>
                    {agent.display_name}
                  </option>
                ))}
              </select>
            )}
            {!loadingAgents && selectedAgentData && (
              <p className="text-xs text-muted-foreground mt-1 truncate" title={selectedAgentData.path}>
                {selectedAgentData.path}
              </p>
            )}
          </div>

          {/* Model Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2 text-foreground">
              Gemini Model
            </label>
            {loadingModels ? (
              <div className="w-full p-3 border rounded-lg bg-background border-primary/30 flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading models...
              </div>
            ) : (
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={isLoading}
                className="w-full p-3 border rounded-lg bg-background text-foreground border-primary/30 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {models.map((model) => {
                  const displayName = model.display_name ||
                    (model.name.includes('/') ? model.name.split('/').pop() : model.name);
                  return (
                    <option key={model.name} value={model.name}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
            )}
            {!loadingModels && selectedModelData && (
              <p className="text-xs text-muted-foreground mt-1">
                {selectedModelData.description}
              </p>
            )}
          </div>

          {/* Session Indicator */}
          {sessionId && (
            <div className="mb-4 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <Link className="h-4 w-4" />
                <span className="font-medium">Conversation Active</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Agent remembers previous messages
              </p>
            </div>
          )}
        </div>

        {/* Chat History */}
        {user && (
          <div className="flex-1 border-t border-primary/20 flex flex-col min-h-0">
            <div className="flex items-center justify-between p-4">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary"
              >
                <History className="h-4 w-4" />
                Chat History ({chatSessions.length})
              </button>
              <button
                onClick={startNewChat}
                className="p-1.5 bg-primary/20 text-primary border border-primary/30 rounded-lg hover:bg-primary/30 transition-colors"
                title="New Chat"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {showHistory && (
              <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
                {chatSessions.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    No previous chats
                  </p>
                ) : (
                  chatSessions.map((session) => (
                    <div
                      key={session.id}
                      className={`group relative p-3 rounded-lg cursor-pointer transition-colors ${
                        currentSessionId === session.id
                          ? 'bg-primary/20 border border-primary/40'
                          : 'bg-card/50 border border-primary/10 hover:border-primary/30'
                      }`}
                      onClick={() => loadSession(session.id)}
                    >
                      <p className="text-sm text-foreground truncate pr-6">{session.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(session.updated_at).toLocaleDateString()} · {session.message_count} msgs
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSession(session.id);
                        }}
                        className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 text-destructive hover:bg-destructive/20 rounded transition-all"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-2xl">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary to-orange-500 flex items-center justify-center">
                  <MessageSquare className="w-12 h-12 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">
                  Ask a Question
                </h2>
                <p className="text-muted-foreground mb-6">
                  Get answers from your data warehouse using natural language
                </p>
                <div className="grid grid-cols-1 gap-3 text-left">
                  <div className="bg-card/50 border border-primary/20 rounded-lg p-4">
                    <p className="text-sm text-foreground">"What is the total revenue?"</p>
                  </div>
                  <div className="bg-card/50 border border-primary/20 rounded-lg p-4">
                    <p className="text-sm text-foreground">"How many employees do we have?"</p>
                  </div>
                  <div className="bg-card/50 border border-primary/20 rounded-lg p-4">
                    <p className="text-sm text-foreground">"Show me the top 10 customers"</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-6">
              {messages
                .filter((message) => !message.pending)  // Don't render pending messages - show thinking indicator instead
                .map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-3xl rounded-lg shadow-md ${
                      message.role === 'user'
                        ? 'bg-gradient-to-r from-primary to-orange-500 text-white'
                        : 'bg-card border border-primary/20'
                    }`}
                  >
                    <div className="px-4 py-3">
                      <p className={`whitespace-pre-wrap ${message.role === 'user' ? 'text-white' : 'text-foreground'}`}>
                        {/* If there's a separate sql field, remove SQL block from content to avoid duplication */}
                        {message.sql && message.role === 'assistant'
                          ? message.content.replace(/```sql[\s\S]*?```/g, '').replace(/SQL QUERY:\s*/g, '').trim()
                          : message.content}
                      </p>

                      {/* SQL Query - Only show the dedicated SQL box if there's a sql field */}
                      {message.sql && message.role === 'assistant' && (
                        <div className="mt-4 border border-primary/30 rounded-lg overflow-hidden">
                          <div className="bg-primary/10 px-3 py-2 border-b border-primary/30">
                            <span className="text-xs font-semibold text-foreground">Generated SQL</span>
                          </div>
                          <div className="bg-slate-900">
                            <Editor
                              value={message.sql}
                              onValueChange={() => {}}
                              highlight={(code) => highlight(code, languages.sql, 'sql')}
                              padding={12}
                              readOnly
                              style={{
                                fontFamily: 'Monaco, Menlo, "Courier New", monospace',
                                fontSize: 13,
                                lineHeight: 1.6,
                                backgroundColor: '#1e1e1e',
                                minHeight: '80px'
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Execution Time */}
                      {message.execution_time_ms !== undefined && message.role === 'assistant' && !message.error && (
                        <div className="mt-3 text-xs text-muted-foreground">
                          Executed in {(message.execution_time_ms / 1000).toFixed(2)}s
                        </div>
                      )}

                      {/* Error */}
                      {message.error && (
                        <div className="mt-3 text-xs text-destructive">
                          Error: {message.error}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Progress Updates - Show pending message content while processing */}
              {isLoading && (() => {
                const pendingMsg = messages.find(m => m.pending);
                // Show progress if there's any content (agent is giving updates)
                if (pendingMsg?.content && pendingMsg.content.length > 0) {
                  return (
                    <div className="flex justify-start">
                      <div className="max-w-3xl rounded-lg bg-card/80 border border-primary/20 px-4 py-3">
                        <p className="text-foreground/90 whitespace-pre-wrap">{pendingMsg.content}</p>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Thinking Indicator - Fancy with smooth fade */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="rounded-xl bg-gradient-to-r from-primary/5 to-orange-500/5 backdrop-blur-sm border border-primary/10 px-5 py-3 flex items-center gap-4 shadow-lg">
                    <div className="relative flex-shrink-0">
                      <div className="absolute inset-0 bg-gradient-to-r from-primary to-orange-500 rounded-full blur-md opacity-30 animate-pulse" />
                      <Loader2 className="w-5 h-5 animate-spin text-primary relative" style={{ animationDuration: '1.5s' }} />
                    </div>
                    <div className="relative overflow-hidden h-6 min-w-[280px]">
                      {THINKING_MESSAGES.map((msg, idx) => (
                        <span
                          key={idx}
                          className={`absolute inset-0 text-sm font-medium bg-gradient-to-r from-primary/80 to-orange-400/80 bg-clip-text text-transparent transition-all duration-700 ease-in-out whitespace-nowrap ${
                            idx === thinkingStage
                              ? 'opacity-100 translate-y-0'
                              : 'opacity-0 translate-y-2'
                          }`}
                        >
                          {msg}
                        </span>
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground/50 tabular-nums flex-shrink-0 min-w-[40px] text-right">{elapsedTime}s</span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-primary/30 bg-card/50 p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-3">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder={selectedAgent ? "Ask a question about your data..." : "Please select an agent first"}
                disabled={isLoading || !selectedAgent}
                className="flex-1 px-4 py-3 border border-primary/30 rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputValue.trim() || !selectedAgent}
                className="px-6 py-3 bg-gradient-to-r from-primary to-orange-500 text-white rounded-lg hover:from-primary/90 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Thinking...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
